// SPDX-License-Identifier: SEE LICENSE
//
// Live single-session stream: fetches an initial snapshot then keeps the
// session, per-pillar telemetry frames, verdicts and certificate state
// up to date over SSE. The dashboard (localhost) reaches the backend
// directly, so no ngrok header is needed here.

import { useEffect, useRef, useState } from 'react';

import { getSession, sessionStreamUrl } from '../api';
import type {
  DemoEvent,
  EnginePulse,
  SessionRecord,
  SessionSummary,
  StepKind,
  TelemetryFrame,
} from './types';

const FRAME_CAP = 1200;
const ACTIVITY_LOG_CAP = 80;

/** A real, observable event during the session — REAL only, no synthesis. */
export type ActivityEventType =
  | 'session.started'
  | 'session.restored'
  | 'session.finalized'
  | 'step.started'
  | 'step.passed'
  | 'step.concern'
  | 'step.failed'
  | 'step.skipped'
  | 'verdict.signed'
  | 'certificate.minted'
  | 'telemetry.burst';

export interface ActivityLine {
  type: ActivityEventType;
  ts: number;
  kind?: StepKind;
  headline?: string;
  detail?: string;
}

export interface LiveSessionState {
  session: SessionRecord | null;
  summary: SessionSummary | null;
  framesByKind: Record<string, TelemetryFrame[]>;
  verdictByKind: Record<string, Record<string, unknown>>;
  certificate: Record<string, unknown> | null;
  connected: boolean;
  /** The pillar that most recently emitted a frame (for "now running" focus). */
  activeKind: StepKind | null;
  /** Total telemetry frames received this session (live counter). */
  frameCount: number;
  /** Wall-clock ms (Date.now) when the last telemetry frame arrived. */
  lastFrameAt: number | null;
  /** Latest REAL engine pulse from the backend (1Hz, only when there's real activity). */
  enginePulse: EnginePulse | null;
  /** Cumulative engine pulses received. */
  engineTicks: number;
  /** Rolling REAL activity log (most recent first). Only real device/backend events. */
  activity: ActivityLine[];
}

export function useSessionStream(sessionId: string | undefined): LiveSessionState {
  const [state, setState] = useState<LiveSessionState>({
    session: null,
    summary: null,
    framesByKind: {},
    verdictByKind: {},
    certificate: null,
    connected: false,
    activeKind: null,
    frameCount: 0,
    lastFrameAt: null,
    enginePulse: null,
    engineTicks: 0,
    activity: [],
  });
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    // Wipe ALL session-scoped state the instant the session id changes.
    // Without this, a tab that auto-redirects from a finalized session to a
    // freshly-started one keeps the previous session's frames / verdicts /
    // certificate in state — so every pillar panel shows the OLD session's
    // data "already populated" while the new session's snapshot is still in
    // flight. Resetting to a clean baseline means a just-started run shows
    // no data until its own real telemetry streams in.
    setState({
      session: null,
      summary: null,
      framesByKind: {},
      verdictByKind: {},
      certificate: null,
      connected: false,
      activeKind: null,
      frameCount: 0,
      lastFrameAt: null,
      enginePulse: null,
      engineTicks: 0,
      activity: [],
    });

    getSession(sessionId)
      .then((snap) => {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          session: snap.session,
          summary: snap.summary,
          framesByKind: snap.frames ?? {},
        }));
      })
      .catch(() => {
        /* stream snapshot will still populate */
      });

    const es = new EventSource(sessionStreamUrl(sessionId));
    esRef.current = es;
    es.onopen = () => setState((s) => ({ ...s, connected: true }));
    es.onerror = () => setState((s) => ({ ...s, connected: false }));
    es.onmessage = (ev) => {
      let evt: DemoEvent & { payload: Record<string, unknown> };
      try {
        evt = JSON.parse(ev.data);
      } catch {
        return;
      }
      setState((prev) => applyEvent(prev, evt));
    };

    return () => {
      cancelled = true;
      es.close();
      esRef.current = null;
    };
  }, [sessionId]);

  return state;
}

function pushActivity(prev: ActivityLine[], line: ActivityLine): ActivityLine[] {
  return [line, ...prev].slice(0, ACTIVITY_LOG_CAP);
}

function deriveStepActivity(
  prevSession: SessionRecord | null,
  nextSession: SessionRecord,
  ts: number,
): ActivityLine[] {
  if (!prevSession) return [];
  const lines: ActivityLine[] = [];
  const prevByKind = new Map(prevSession.steps.map((s) => [s.kind, s.status]));
  for (const step of nextSession.steps) {
    const before = prevByKind.get(step.kind);
    if (before === step.status) continue;
    const headline =
      (step.summary?.headline as string | undefined) ?? `${step.kind} ${step.status}`;
    if (step.status === 'active') {
      lines.push({ type: 'step.started', ts, kind: step.kind, headline });
    } else if (step.status === 'passed') {
      lines.push({ type: 'step.passed', ts, kind: step.kind, headline });
    } else if (step.status === 'concern') {
      lines.push({ type: 'step.concern', ts, kind: step.kind, headline });
    } else if (step.status === 'failed') {
      lines.push({ type: 'step.failed', ts, kind: step.kind, headline });
    } else if (step.status === 'skipped') {
      lines.push({ type: 'step.skipped', ts, kind: step.kind, headline });
    }
  }
  return lines;
}

function applyEvent(prev: LiveSessionState, evt: DemoEvent): LiveSessionState {
  switch (evt.type) {
    case 'snapshot': {
      const p = evt.payload as {
        session: SessionRecord;
        summary: SessionSummary;
        frames: Record<string, TelemetryFrame[]>;
      };
      // Seed the activity log from the snapshot's existing step states
      // so a tab connecting mid-run sees the prior history.
      const seed: ActivityLine[] = [];
      if (p.session) {
        seed.push({
          type: 'session.started',
          ts: p.session.startedAt,
          headline: p.session.deviceModel ?? 'device session opened',
        });
        for (const s of p.session.steps) {
          if (s.status === 'passed' || s.status === 'concern' || s.status === 'failed') {
            seed.push({
              type:
                s.status === 'passed'
                  ? 'step.passed'
                  : s.status === 'concern'
                    ? 'step.concern'
                    : 'step.failed',
              ts: s.completedAt ?? evt.ts,
              kind: s.kind,
              headline: (s.summary?.headline as string | undefined) ?? s.kind,
            });
          } else if (s.status === 'active') {
            seed.push({
              type: 'step.started',
              ts: s.startedAt ?? evt.ts,
              kind: s.kind,
            });
          }
        }
        if (p.session.finalizedAt) {
          seed.push({ type: 'session.finalized', ts: p.session.finalizedAt });
        }
      }
      // newest first for the log column
      seed.sort((a, b) => b.ts - a.ts);
      // REPLACE frames + verdicts on snapshot (not spread). Otherwise a
      // dashboard tab that was viewing an old session and is now hooked
      // to a new sessionId keeps the old session's frames around — the
      // "Battery 94.2% SoH" value pre-populates on the new session
      // before the test even runs. Snapshot is the source of truth.
      return {
        ...prev,
        session: p.session ?? prev.session,
        summary: p.summary ?? prev.summary,
        framesByKind: p.frames ?? {},
        verdictByKind: {},
        certificate: null,
        enginePulse: null,
        frameCount: 0,
        activity: seed.slice(0, ACTIVITY_LOG_CAP),
      };
    }
    case 'session.update': {
      const p = evt.payload as { session?: SessionRecord; reason?: string };
      if (!p.session) return prev;
      let activity = prev.activity;
      const stepLines = deriveStepActivity(prev.session, p.session, evt.ts);
      for (const line of stepLines) activity = pushActivity(activity, line);
      if (p.reason === 'finalized' && p.session.finalizedAt) {
        activity = pushActivity(activity, {
          type: 'session.finalized',
          ts: p.session.finalizedAt,
        });
      }
      if (p.reason === 'restored' && !prev.session) {
        activity = pushActivity(activity, {
          type: 'session.started',
          ts: p.session.startedAt,
          headline: p.session.deviceModel ?? 'device session opened',
        });
      }
      return { ...prev, session: p.session, activity };
    }
    case 'telemetry': {
      const kind = String((evt.payload as { kind?: string }).kind ?? 'unknown');
      const frame = (evt.payload as { frame?: TelemetryFrame }).frame;
      if (!frame) return prev;
      const existing = prev.framesByKind[kind] ?? [];
      const next = existing.concat(frame);
      if (next.length > FRAME_CAP) next.splice(0, next.length - FRAME_CAP);
      // Burst marker: every 25 frames per pillar, log a real "still
      // streaming" event. Honest and useful, not spammy.
      let activity = prev.activity;
      if (next.length > 0 && next.length % 25 === 0) {
        activity = pushActivity(activity, {
          type: 'telemetry.burst',
          ts: evt.ts,
          kind: kind as StepKind,
          detail: `${next.length} frames received`,
        });
      }
      return {
        ...prev,
        activeKind: kind as StepKind,
        framesByKind: { ...prev.framesByKind, [kind]: next },
        frameCount: prev.frameCount + 1,
        lastFrameAt: Date.now(),
        activity,
      };
    }
    case 'run.ingested': {
      const p = evt.payload as { kind?: string; verdict?: string; headline?: string };
      const kind = String(p.kind ?? 'unknown');
      const line: ActivityLine = {
        type: 'verdict.signed',
        ts: evt.ts,
        kind: kind as StepKind,
      };
      if (p.headline) line.headline = p.headline;
      if (p.verdict) line.detail = p.verdict;
      const activity = pushActivity(prev.activity, line);
      return {
        ...prev,
        verdictByKind: { ...prev.verdictByKind, [kind]: evt.payload },
        activity,
      };
    }
    case 'certificate.minted': {
      const p = evt.payload as { rootHex?: string; rekorId?: string };
      const line: ActivityLine = {
        type: 'certificate.minted',
        ts: evt.ts,
        headline: 'Digital Device Certificate minted',
      };
      if (p.rootHex) line.detail = `root=0x${p.rootHex.slice(0, 12)}…`;
      const activity = pushActivity(prev.activity, line);
      return { ...prev, certificate: evt.payload, activity };
    }
    case 'engine.tick': {
      const p = evt.payload as Record<string, unknown>;
      const pulse: EnginePulse = {
        uptimeMs: Number(p.uptimeMs ?? 0),
        framesTotal: Number(p.framesTotal ?? 0),
        framesPerSec: Number(p.framesPerSec ?? 0),
        bytesTotal: Number(p.bytesTotal ?? 0),
        verdictsSigned: Number(p.verdictsSigned ?? 0),
        activeKinds: (p.activeKinds as StepKind[]) ?? [],
        fpsHistory: (p.fpsHistory as number[]) ?? [],
        fleetSessions: Number(p.fleetSessions ?? 0),
        fleetDevices: Number(p.fleetDevices ?? 0),
        ts: evt.ts,
      };
      return {
        ...prev,
        enginePulse: pulse,
        engineTicks: prev.engineTicks + 1,
      };
    }
    default:
      return prev;
  }
}
